import React, { useMemo, useState } from "react";
import { Select } from "antd";
import clsx from "clsx";
import styles from "./CustomMultiSelect.module.scss";

const { Option } = Select;

// Sentinel value for the synthetic "Select All" option — never present in
// caller data, so it can't collide with a real option value.
const SELECT_ALL_VALUE = "__custom_multi_select_all__";

const CustomMultiSelect = ({
    data = [],
    placeholder = "Select options",
    value: externalValue,
    onChange,
    allowClear = true,
    className = "",
    style = {},
    showSearch = true,
    disabled,
}) => {
    const [internalValue, setInternalValue] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Same substring match previously done via antd's `filterOption` —
    // done manually here so the "Select All" option can select exactly the
    // set of options currently matching the search, not the full dataset.
    const filteredData = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return data;
        return data.filter((item) =>
            String(item.searchLabel ?? item.label ?? "")
                .toLowerCase()
                .includes(query)
        );
    }, [data, searchText]);

    const handleChange = (val) => {
        // antd clears its own search input on selection (autoClearSearchValue
        // defaults to true), but that doesn't touch our separate `searchText`
        // state — left stale, it keeps filtering `data` against whatever was
        // last typed, even after the caller's `data` prop changes underneath
        // (e.g. a Unit/Business Sector filter narrowing this list), silently
        // showing "No data" for options that are actually present.
        setSearchText("");

        if (val.includes(SELECT_ALL_VALUE)) {
            const withoutSentinel = val.filter((v) => v !== SELECT_ALL_VALUE);
            const filteredValues = filteredData.map((item) => item.value);
            const merged = Array.from(new Set([...withoutSentinel, ...filteredValues]));
            setInternalValue(merged);
            if (onChange) onChange(merged);
            return;
        }

        setInternalValue(val);
        if (onChange) onChange(val);
    };

    const finalValue =
        externalValue !== undefined
            ? externalValue
            : internalValue;

    const combinedClassName = clsx(className, {
        "custom-select-selected":
            finalValue && finalValue.length,
    });

    return (
        <Select
            mode="multiple"
            maxTagCount={1}
            showSearch={showSearch}
            placeholder={placeholder}
            allowClear={allowClear}
            value={finalValue}
            onChange={handleChange}
            onSearch={setSearchText}
            // Filtering is done manually above (see filteredData) so the
            // rendered option list and "Select All" stay in sync with
            // whatever the user has typed.
            filterOption={false}
            // Without this, a selected tag renders Option's `children` (the
            // full rich label — e.g. a two-line project name + IO number),
            // which overflows and gets visually clipped inside the compact
            // tag. `label` is already a plain string on every Option
            // (searchLabel, falling back to label when it's a string), so
            // use that for the tag instead.
            optionLabelProp="label"
            className={`custom-multi-select custom-select-small ${combinedClassName}`}
            style={{
                height: "42px",
                ...style,
            }}
            size="middle"
            disabled={disabled}
        >
            {filteredData.length > 0 && (
                <Option
                    key={SELECT_ALL_VALUE}
                    value={SELECT_ALL_VALUE}
                    className="custom-multi-select-all-option"
                >
                    Select All
                </Option>
            )}
            {filteredData.map((item) => (
                <Option
                    key={item.value}
                    value={item.value}
                    // A plain-text override for search when `label` is a rich
                    // node (e.g. JSX with an italic sub-line) instead of a
                    // string — falls back to `label` itself when it's
                    // already a string, so existing callers are unaffected.
                    label={item.searchLabel ?? item.label}
                    // antd only auto-derives the native hover tooltip from a
                    // string `children`; a JSX label (like Package's italic
                    // sub-line) has no plain text to show, so the tooltip
                    // silently doesn't appear. Set it explicitly from the
                    // same plain-text value used for search.
                    title={item.searchLabel ?? (typeof item.label === "string" ? item.label : undefined)}
                >
                    {item.label}
                </Option>
            ))}
        </Select>
    );


};

export default CustomMultiSelect;
